import React, { useState, useContext, useEffect, useMemo } from "react";
import { Collapse } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { TriangleUpIcon } from "@components/icons";
import { styles } from "@components/styles";
import useFilter from "@hooks/useFilter";
import { constants } from "../context/constants";
import { DataScienceContext } from "../context/DataScienceContext";

function AreaList({ project }) {
  const { filterState, filterDispatch, setActiveRain, areaColorMap } =
    useContext(DataScienceContext);
  const { areasId, areas, treesId, installationsId, rainAreaId } = filterState;
  const [filterTerm, setFilterTerm] = useState("");
  const [open, setOpen] = useState(true);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (project?.areas) {
      filterDispatch({
        type: constants.SET_AREAS,
        payload: project.areas,
      });
    }
  }, [project, filterDispatch]);

  const filteredAreas = useMemo(
    () => useFilter({ filterTerm, filterItem: areas }),
    [filterTerm, areasId, areas]
  );

  // checks whether all areas are selected whenever areasId changes
  useEffect(() => {
    const allSelected = areas.length > 0 && areas.every((area) => areasId.includes(area.id));
    setSelectAllChecked(allSelected);
  }, [areasId, areas]);

  useEffect(() => {
    if (rainAreaId != null && !areasId.includes(rainAreaId)) {
      filterDispatch({ type: constants.SET_RAIN_AREA, payload: null });
      setActiveRain(false);
    }
  }, [rainAreaId, areasId, filterDispatch, setActiveRain]);

  //4. This function used to set the value for tree search on key change to input field.
  function updateFilterHandler(event) {
    setFilterTerm(event.target.value);
  }

  //5. Check and uncheck functionalities to set and filter sensor from sensor list
  const handleChange = (props) => {
    const { e, area } = props;
    if (e.target.checked) {
      filterDispatch({ type: constants.SET_CHECKED_AREAS, payload: [area.id] });
    }
    if (e.target.checked === false) {
      if (rainAreaId === area.id) {
        filterDispatch({ type: constants.SET_RAIN_AREA, payload: null });
        setActiveRain(false);
      }
      //1.filter  area on area unchecked
      const filteredArea = areasId.filter((id) => id !== area.id);
      filterDispatch({ type: constants.RESET_CHECKED_AREAS, payload: filteredArea });

      //2. filter tree on area unchecked
      const filteredTree = treesId.filter((id) => {
        const arr = area.tree_set.map((tree) => tree.id);
        return !arr.includes(id);
      });
      filterDispatch({ type: constants.RESET_CHECKED_TREES, payload: filteredTree });

      //3.filter particular area->tree sensors on area un checked
      const filteredInstallation = installationsId.filter((id) => {
        const arr = area.tree_set.flatMap((tree) =>
          (tree.installations || []).map((inst) => inst.id)
        );
        return !arr.includes(id);
      });
      filterDispatch({
        type: constants.RESET_CHECKED_INSTALLATIONS,
        payload: filteredInstallation,
      });
    }
  };

  // 6.
  const handleSelectAll = ({ e, filteredAreas }) => {
    if (e.target.checked) {
      const ids = filteredAreas.map((area) => area.id);
      filterDispatch({ type: constants.RESET_CHECKED_AREAS, payload: ids });
      setSelectAllChecked(true);
    } else {
      filterDispatch({ type: constants.RESET_CHECKED_AREAS, payload: [] });
      filterDispatch({ type: constants.RESET_CHECKED_TREES, payload: [] });
      filterDispatch({ type: constants.RESET_CHECKED_INSTALLATIONS, payload: [] });
      filterDispatch({ type: constants.SET_RAIN_AREA, payload: null });
      setActiveRain(false);
      setSelectAllChecked(false);
    }
  };

  // 7. Handle rain selection for area
  const handleRainSelection = (areaId) => {
    const newRainAreaId = rainAreaId === areaId ? null : areaId;
    filterDispatch({ type: constants.SET_RAIN_AREA, payload: newRainAreaId });

    // Set rain state (but don't trigger fetch - that happens on loadData button click)
    if (newRainAreaId !== null) {
      setActiveRain(true);
    } else {
      setActiveRain(false);
    }
  };

  return (
    <div className="me-3" data-tour="ds-filter-areas">
      <div className="d-flex pe-2 justify-content-between">
        <div
          className={styles.dsFilterTitle}
          onClick={() => setOpen(!open)}
          aria-controls="example-collapse-text"
          aria-expanded={open}
        >
          <TriangleUpIcon open={open} /> <span className=" ds-filter-title">{t("areas")}</span>
        </div>
        <div>
          <input
            type="checkbox"
            checked={selectAllChecked}
            onChange={(e) => handleSelectAll({ e, filteredAreas })}
          />
        </div>
      </div>
      <Collapse in={open}>
        <div>
          <div className="py-2 pe-2">
            <input
              type="text"
              className="form-control filter-search"
              placeholder={t("search_area")}
              onChange={updateFilterHandler}
            />
          </div>
          <ul className="p-0 pe-2 filter__list" id="example-collapse-text">
            {filteredAreas?.map((area) => {
              const rainButtonBg =
                rainAreaId === area.id ? "var(--color-bg-subtle, #a8a9aa)" : "transparent";

              const areaColor = areaColorMap[area.id];
              const isSelected = areasId.includes(area.id);

              return (
                <li
                  key={area.id}
                  className={`filter__items  gap-1 d-flex justify-content-between align-items-center ps-1`}
                >
                  <span>{area.name}</span>
                  <div className="d-flex gap-2 align-items-center">
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => handleRainSelection(area.id)}
                      title={
                        rainAreaId === area.id
                          ? "Rain active for this area"
                          : "Select rain for this area"
                      }
                      style={{
                        fontSize: "12px",
                        padding: "2px 8px",
                        minWidth: "auto",
                        border: "none",
                        backgroundColor: rainButtonBg,
                        color: "var(--color-text-primary, #333)",
                      }}
                    >
                      {rainAreaId === area.id ? "🌧️" : "☔"}
                    </button>
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        handleChange({ e, area });
                      }}
                      style={{
                        background: isSelected
                          ? areaColor || "var(--bs-primary, #0d6efd)"
                          : "var(--color-bg-surface, #ffffff)",
                        border: isSelected
                          ? `2px solid ${areaColor || "var(--bs-primary, #0d6efd)"}`
                          : "1px solid var(--color-border-strong, #888888)",
                        outline: "none",
                        cursor: "pointer",
                      }}
                      checked={isSelected}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </Collapse>
    </div>
  );
}

export default React.memo(AreaList);
